import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { BrightBlueCatalogOperator } from './types';

export interface IntegrationConfig {
  id: string;
  isEnabled: boolean;
  providerTenantId: string | null;
  webhookUrl: string;
  hasWebhookSecret: boolean;
  syncProducts: boolean;
  syncMachines: boolean;
  syncTransactions: boolean;
  autoCreateProducts: boolean;
  cloudBaseUrl: string;
  createdAt: string;
  updatedAt: string;
  recentEvents: WebhookEventSummary[];
}

export interface WebhookEventSummary {
  id: string;
  eventId: string;
  eventType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  error?: string | null;
}

interface BrightBlueIntegrationProps {
  config: IntegrationConfig | null;
  catalogOperators: BrightBlueCatalogOperator[];
  defaultOperatorId: string | null;
  needsOperatorLink?: boolean;
  onEnable: (tenantId: string) => Promise<{ webhookSecret?: string }>;
  onUpdate: (updates: Partial<IntegrationConfig>) => Promise<void>;
  onDisable: () => Promise<void>;
  onRegenerateSecret: () => Promise<{ webhookSecret: string }>;
  onViewAllEvents: () => void;
}

export function BrightBlueIntegration({
  config,
  catalogOperators,
  defaultOperatorId,
  needsOperatorLink = false,
  onEnable,
  onUpdate,
  onDisable,
  onRegenerateSecret,
  onViewAllEvents,
}: BrightBlueIntegrationProps) {
  const [tenantId, setTenantId] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [enabling, setEnabling] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [cloudBaseUrl, setCloudBaseUrl] = useState(config?.cloudBaseUrl ?? '');
  const [cloudUrlError, setCloudUrlError] = useState<string | null>(null);

  useEffect(() => {
    setCloudBaseUrl(config?.cloudBaseUrl ?? '');
  }, [config?.cloudBaseUrl]);

  useEffect(() => {
    if (catalogOperators.length === 0) return;
    setTenantId((current) => {
      if (current && catalogOperators.some((op) => op.id === current)) {
        return current;
      }
      if (defaultOperatorId && catalogOperators.some((op) => op.id === defaultOperatorId)) {
        return defaultOperatorId;
      }
      return catalogOperators[0]?.id ?? '';
    });
  }, [catalogOperators, defaultOperatorId]);

  const selectedOperator = catalogOperators.find((op) => op.id === tenantId);
  const hasOperatorChoices = catalogOperators.length > 0;

  const handleEnable = async () => {
    if (!tenantId.trim()) return;
    setEnabling(true);
    try {
      const result = await onEnable(tenantId.trim());
      if (result.webhookSecret) {
        setNewSecret(result.webhookSecret);
        setShowSecret(true);
      }
      setSetupOpen(false);
      setTenantId('');
    } finally {
      setEnabling(false);
    }
  };

  const handleToggleSync = async (field: 'syncProducts' | 'syncMachines' | 'syncTransactions' | 'autoCreateProducts', value: boolean) => {
    setUpdating(true);
    try {
      await onUpdate({ [field]: value });
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleEnabled = async () => {
    setUpdating(true);
    try {
      await onUpdate({ isEnabled: !config?.isEnabled });
    } finally {
      setUpdating(false);
    }
  };

  const handleRegenerateSecret = async () => {
    const result = await onRegenerateSecret();
    setNewSecret(result.webhookSecret);
    setShowSecret(true);
  };

  const handleDisable = async () => {
    await onDisable();
    setConfirmDisable(false);
  };

  const saveCloudBaseUrl = async () => {
    if (!config) return;
    const next = cloudBaseUrl.trim();
    if (next === (config.cloudBaseUrl ?? '')) return;
    setUpdating(true);
    setCloudUrlError(null);
    try {
      await onUpdate({ cloudBaseUrl: next });
    } catch (err) {
      setCloudUrlError(err instanceof Error ? err.message : 'Failed to save Cloud URL');
    } finally {
      setUpdating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString();
  };

  const getStatusBadge = (status: WebhookEventSummary['status']) => {
    const variants: Record<WebhookEventSummary['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
      COMPLETED: 'default',
      PROCESSING: 'secondary',
      PENDING: 'outline',
      FAILED: 'destructive',
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  if (!config) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Bright.Blue Integration</CardTitle>
              <CardDescription>Connect your Bright.Blue vending platform</CardDescription>
            </div>
            <Badge variant="outline">Not Configured</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
            <DialogTrigger asChild>
              <Button>Set Up Integration</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Connect Bright.Blue</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {needsOperatorLink && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Catalog is imported but operators are not linked yet. Choose the same export
                    file above (operators load automatically), or re-run import — it upserts existing
                    data and will not duplicate products or machines.
                  </div>
                )}
                <div>
                  <Label htmlFor="tenantId">Bright.Blue operator</Label>
                  {hasOperatorChoices ? (
                    <Select value={tenantId} onValueChange={setTenantId}>
                      <SelectTrigger id="tenantId">
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogOperators.map((op) => (
                          <SelectItem key={op.id} value={op.id}>
                            {op.name}
                            {op.parentVendorId ? ' (child)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="tenantId"
                      value={tenantId}
                      onChange={(e) => setTenantId(e.target.value)}
                      placeholder="operator-uuid"
                    />
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    {hasOperatorChoices
                      ? 'Operators from your catalog export. This ID must match webhook tenantContext.vendorId in Bright.Blue.'
                      : needsOperatorLink
                        ? 'Link operators by selecting your export file above, or re-import once.'
                        : 'Choose a catalog export file above to load operators, or paste the operator UUID from Bright.Blue.'}
                  </p>
                  {selectedOperator && (
                    <p className="text-xs text-muted-foreground mt-1 font-mono">{selectedOperator.id}</p>
                  )}
                </div>
                <Button onClick={handleEnable} disabled={!tenantId.trim() || enabling}>
                  {enabling ? 'Connecting...' : 'Connect'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Bright.Blue Integration</CardTitle>
            <CardDescription>Sync products, machines, and transactions</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={config.isEnabled ? 'default' : 'secondary'}>
              {config.isEnabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleEnabled}
              disabled={updating}
            >
              {config.isEnabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          <div>
            <Label className="text-sm font-medium">Operator</Label>
            <p className="text-sm text-muted-foreground">
              {catalogOperators.find((op) => op.id === config.providerTenantId)?.name ??
                config.providerTenantId}
            </p>
            {config.providerTenantId && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">{config.providerTenantId}</p>
            )}
          </div>

          <div>
            <Label className="text-sm font-medium">Webhook URL</Label>
            <div className="flex items-center gap-2">
              <code className="text-sm bg-muted px-2 py-1 rounded flex-1 overflow-hidden text-ellipsis">
                {config.webhookUrl}
              </code>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(config.webhookUrl)}>
                Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              In Bright.Blue Admin → Webhooks, create a subscription to this URL with events{' '}
              <code className="text-xs">product.*</code>, <code className="text-xs">category.*</code>, and{' '}
              <code className="text-xs">machine.*</code>. Use the webhook secret below for HMAC signing.
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium">Webhook Secret</Label>
            <div className="flex items-center gap-2">
              {showSecret && newSecret ? (
                <>
                  <code className="text-sm bg-muted px-2 py-1 rounded flex-1 font-mono">
                    {newSecret}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(newSecret)}>
                    Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowSecret(false)}>
                    Hide
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-sm text-muted-foreground">
                    {config.hasWebhookSecret ? '••••••••••••••••••••' : 'Not set'}
                  </span>
                  <Button variant="outline" size="sm" onClick={handleRegenerateSecret}>
                    Regenerate
                  </Button>
                </>
              )}
            </div>
            {showSecret && newSecret && (
              <p className="text-sm text-amber-600 mt-1">
                Save this secret now. It will not be shown again.
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Machine and pick-list sync send this same secret as the Cloud API key.
            </p>
          </div>

          <div>
            <Label htmlFor="cloudBaseUrl" className="text-sm font-medium">
              Cloud URL
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="cloudBaseUrl"
                value={cloudBaseUrl}
                onChange={(e) => setCloudBaseUrl(e.target.value)}
                placeholder="https://cloud-host"
                disabled={updating}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void saveCloudBaseUrl()}
                disabled={updating}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Host Express calls for machine and stock sync. No trailing slash.
            </p>
            {cloudUrlError ? (
              <p className="text-sm text-destructive mt-1" role="alert">
                {cloudUrlError}
              </p>
            ) : null}
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium mb-3 block">Sync Settings</Label>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="syncProducts"
                checked={config.syncProducts}
                onCheckedChange={(checked) => handleToggleSync('syncProducts', !!checked)}
                disabled={updating}
              />
              <Label htmlFor="syncProducts" className="font-normal cursor-pointer">
                Products
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="syncMachines"
                checked={config.syncMachines}
                onCheckedChange={(checked) => handleToggleSync('syncMachines', !!checked)}
                disabled={updating}
              />
              <Label htmlFor="syncMachines" className="font-normal cursor-pointer">
                Machines
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="syncTransactions"
                checked={config.syncTransactions}
                onCheckedChange={(checked) => handleToggleSync('syncTransactions', !!checked)}
                disabled={updating}
              />
              <Label htmlFor="syncTransactions" className="font-normal cursor-pointer">
                Transactions
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="autoCreateProducts"
                checked={config.autoCreateProducts}
                onCheckedChange={(checked) => handleToggleSync('autoCreateProducts', !!checked)}
                disabled={updating}
              />
              <Label htmlFor="autoCreateProducts" className="font-normal cursor-pointer">
                Auto-create products from Bright.Blue
              </Label>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Recent Webhook Events</Label>
            <Button variant="link" size="sm" onClick={onViewAllEvents} className="h-auto p-0">
              View All Events
            </Button>
          </div>
          {config.recentEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No webhook events received yet.</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-2 font-medium">Event Type</th>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-left p-2 font-medium">Time</th>
                    <th className="text-left p-2 font-medium">Event ID</th>
                  </tr>
                </thead>
                <tbody>
                  {config.recentEvents.map((event) => (
                    <tr key={event.id} className="border-t">
                      <td className="p-2 font-mono text-xs">{event.eventType}</td>
                      <td className="p-2">{getStatusBadge(event.status)}</td>
                      <td className="p-2">{formatTime(event.createdAt)}</td>
                      <td className="p-2 font-mono text-xs truncate max-w-[120px]" title={event.eventId}>
                        {event.eventId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <Dialog open={confirmDisable} onOpenChange={setConfirmDisable}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm">
                Remove Integration
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Bright.Blue Integration?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                This will delete the integration configuration. Webhook events will no longer be processed.
                You can set up the integration again at any time.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setConfirmDisable(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDisable}>
                  Remove Integration
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
}

export default BrightBlueIntegration;
